const ChemotherapyUnit4Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <!-- 1. Chapter Header -->
    <div>
        <h2 class="text-2xl font-black text-blue-900 mb-3 border-b-2 border-slate-200 pb-2">Unit IV: Chemotherapy & Immunopharmacology</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            This unit transitions from microbial infections to the chemotherapy of urinary tract infections, sexually transmitted diseases, and malignancies. It further explores the manipulation of the immune system through Immunopharmacology.
        </p>
    </div>

    <!-- 2. UTI & STDs -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-4 border-b border-slate-100 pb-2">1. Chemotherapy of UTI and STDs</h3>
        
        <!-- UTI -->
        <div class="mb-6">
            <h4 class="text-[16px] font-bold text-blue-700 mb-2">A. Urinary Tract Infections (UTIs)</h4>
            <p class="text-[13px] text-slate-700 mb-3">UTIs are predominantly caused by Gram-negative bacteria (e.g., <em>Escherichia coli</em>). Treatment relies on drugs that concentrate well in the urine.</p>
            <ul class="list-disc pl-5 space-y-3 text-[13px] text-slate-600">
                <li>
                    <strong>Nitrofurantoin:</strong> A urinary antiseptic. It is reduced by bacterial flavoproteins to reactive intermediates that damage bacterial DNA and ribosomal proteins. It works only in the lower urinary tract because it is rapidly excreted and concentrated in urine. <span class="text-red-600">ADR: Pulmonary toxicity, brown-colored urine.</span>
                </li>
                <li>
                    <strong>Fluoroquinolones (e.g., Ciprofloxacin, Norfloxacin):</strong> Highly effective for both lower and upper UTIs (pyelonephritis) due to excellent tissue penetration and broad-spectrum activity.
                </li>
                <li>
                    <strong>Cotrimoxazole:</strong> Used for uncomplicated UTIs.
                </li>
            </ul>
        </div>

        <!-- STDs -->
        <div>
            <h4 class="text-[16px] font-bold text-blue-700 mb-2">B. Sexually Transmitted Diseases (STDs)</h4>
            <div class="overflow-x-auto rounded-lg border border-slate-200 mt-2">
                <table class="min-w-full bg-white text-[13px] text-left">
                    <thead class="bg-slate-100 text-slate-700">
                        <tr>
                            <th class="px-4 py-2 font-bold">Disease</th>
                            <th class="px-4 py-2 font-bold">Causative Organism</th>
                            <th class="px-4 py-2 font-bold">Drug of Choice</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100 text-slate-600">
                        <tr>
                            <td class="px-4 py-3 font-bold">Syphilis</td>
                            <td class="px-4 py-3"><em>Treponema pallidum</em></td>
                            <td class="px-4 py-3 text-blue-900 font-bold">Benzathine Penicillin G (IM)</td>
                        </tr>
                        <tr>
                            <td class="px-4 py-3 font-bold">Gonorrhea</td>
                            <td class="px-4 py-3"><em>Neisseria gonorrhoeae</em></td>
                            <td class="px-4 py-3 text-blue-900 font-bold">Ceftriaxone (IM) + Azithromycin</td>
                        </tr>
                        <tr>
                            <td class="px-4 py-3 font-bold">Chlamydia</td>
                            <td class="px-4 py-3"><em>Chlamydia trachomatis</em></td>
                            <td class="px-4 py-3 text-blue-900 font-bold">Azithromycin or Doxycycline</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <!-- 3. Chemotherapy of Malignancy -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-3">2. Chemotherapy of Malignancy (Anticancer Drugs)</h3>
        <p class="text-[13px] text-slate-700 mb-4">Anticancer drugs kill rapidly dividing cells. They lack high selective toxicity, which leads to severe side effects like <strong>bone marrow suppression, alopecia (hair loss), and severe GI distress</strong>.</p>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <!-- Alkylating Agents -->
            <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
                <h4 class="text-[15px] font-bold text-blue-700 mb-1">A. Alkylating Agents</h4>
                <p class="text-[12px] text-slate-600 mb-2"><em>Examples:</em> Cyclophosphamide, Cisplatin.</p>
                <p class="text-[12px] text-slate-700"><strong>MOA:</strong> They form highly reactive carbonium ions that transfer alkyl groups to the N7 position of guanine in DNA. This causes cross-linking of DNA strands, preventing replication and transcription, leading to cell death. (Cell-cycle non-specific).</p>
                <p class="text-[11px] text-red-600 mt-1"><em>ADR: Cyclophosphamide causes Hemorrhagic Cystitis (prevented by giving MESNA).</em></p>
            </div>

            <!-- Antimetabolites -->
            <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
                <h4 class="text-[15px] font-bold text-blue-700 mb-1">B. Antimetabolites</h4>
                <p class="text-[12px] text-slate-600 mb-2"><em>Examples:</em> Methotrexate, 5-Fluorouracil (5-FU).</p>
                <p class="text-[12px] text-slate-700"><strong>MOA:</strong> They act as structural analogs of normal cellular metabolites (folic acid, purines, pyrimidines). <br><strong>Methotrexate:</strong> Inhibits Dihydrofolate Reductase (DHFR), blocking purine/pyrimidine synthesis.<br><strong>5-FU:</strong> Inhibits Thymidylate Synthase. (Cell-cycle specific - S phase).</p>
            </div>

            <!-- Microtubule Inhibitors -->
            <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
                <h4 class="text-[15px] font-bold text-blue-700 mb-1">C. Microtubule Inhibitors</h4>
                <p class="text-[12px] text-slate-600 mb-2"><em>Examples:</em> Vincristine, Paclitaxel.</p>
                <p class="text-[12px] text-slate-700"><strong>MOA:</strong> They disrupt the mitotic spindle required for cell division (M phase specific).<br><strong>Vinca Alkaloids:</strong> Inhibit tubulin polymerization (halt assembly).<br><strong>Taxanes:</strong> Prevent microtubule depolymerization (halt disassembly).</p>
            </div>

            <!-- Cytotoxic Antibiotics -->
            <div class="bg-slate-50 p-4 border border-slate-200 rounded-lg">
                <h4 class="text-[15px] font-bold text-blue-700 mb-1">D. Cytotoxic Antibiotics</h4>
                <p class="text-[12px] text-slate-600 mb-2"><em>Examples:</em> Doxorubicin, Bleomycin.</p>
                <p class="text-[12px] text-slate-700"><strong>MOA:</strong> Doxorubicin intercalates between DNA base pairs and inhibits Topoisomerase II, leading to DNA strand breaks.</p>
                <p class="text-[11px] text-red-600 mt-1"><em>ADR: Doxorubicin causes severe Cardiotoxicity. Bleomycin causes Pulmonary fibrosis.</em></p>
            </div>
        </div>
    </div>

    <!-- 4. Immunopharmacology -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-4 border-b border-slate-100 pb-2">3. Immunopharmacology</h3>
        
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Immunostimulants -->
            <div>
                <h4 class="text-[16px] font-bold text-green-700 mb-2">A. Immunostimulants</h4>
                <p class="text-[13px] text-slate-700 mb-2">Used to enhance immune responses in immunodeficiency disorders or certain cancers.</p>
                <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-600">
                    <li><strong>Levamisole:</strong> Restores depressed immune function by stimulating T-cells and macrophage phagocytosis.</li>
                    <li><strong>BCG (Bacillus Calmette-Guérin):</strong> Acts as a non-specific immunostimulant. Used intravesically for early-stage bladder cancer.</li>
                    <li><strong>Interferons (IFN-&alpha;):</strong> Cytokines with antiviral, antiproliferative, and immunomodulatory effects (used in Hepatitis B/C and leukemias).</li>
                </ul>
            </div>

            <!-- Immunosuppressants -->
            <div>
                <h4 class="text-[16px] font-bold text-red-700 mb-2">B. Immunosuppressants</h4>
                <p class="text-[13px] text-slate-700 mb-2">Crucial for preventing solid organ transplant rejection and managing autoimmune diseases.</p>
                <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-600">
                    <li><strong>Calcineurin Inhibitors (Cyclosporine, Tacrolimus):</strong> MOA: Bind to cyclophilin/FKBP, inhibiting calcineurin. This blocks the transcription of Interleukin-2 (IL-2), preventing T-cell activation. <em>(Highly nephrotoxic)</em>.</li>
                    <li><strong>mTOR Inhibitors (Sirolimus):</strong> Blocks the cellular response to IL-2.</li>
                    <li><strong>Antimetabolites (Azathioprine, Mycophenolate Mofetil):</strong> Inhibit purine synthesis, selectively halting B and T lymphocyte proliferation.</li>
                    <li><strong>Glucocorticoids (Prednisone):</strong> Broadly suppress multiple inflammatory cytokines.</li>
                </ul>
            </div>
        </div>

        <!-- Biologicals & Monoclonal Antibodies -->
        <div class="mt-6 bg-blue-50 p-5 rounded-lg border border-blue-200">
            <h4 class="text-[16px] font-bold text-blue-900 mb-2">C. Protein Drugs, Monoclonal Antibodies & Biosimilars</h4>
            <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-700">
                <li><strong>Monoclonal Antibodies (mAbs):</strong> Laboratory-produced molecules engineered to serve as substitute antibodies. They act as <strong>target drugs to antigens</strong>.
                    <br><em>Example:</em> <strong>Infliximab</strong> (Targets and neutralizes TNF-&alpha; for Rheumatoid Arthritis), <strong>Trastuzumab</strong> (Targets HER2 receptors in breast cancer).
                </li>
                <li><strong>Protein Drugs:</strong> Recombinant proteins used therapeutically, such as synthetic Insulin, Erythropoietin, or Filgrastim (G-CSF).</li>
                <li><strong>Biosimilars:</strong> A biologic medical product that is highly similar to another already approved biological medicine (the 'reference product'). Unlike generic chemical drugs, biologics are complex and cannot be copied exactly, hence they are "biosimilar".</li>
            </ul>
        </div>
    </div>

</div>
`;

export default ChemotherapyUnit4Notes;